// src/App.jsx — 수정본 v3
// 수정사항:
//   1. 역할 전환 즉시 반영 (구직자↔업체 배지 + 메뉴 변경)
//   2. 샘플 공고 [샘플] 뱃지 표시
//   3. 프로필 수정에 비밀번호 변경 기능 추가

import { useState, useCallback, useEffect } from "react";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { useFilteredJobs } from "./hooks/useJobs";
import { applyToJob, closeJob, deleteJob, createJob, updateJob } from "./services/jobsService";
import { logOut, updateUserProfile, switchRole } from "./services/authService";
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";
import { auth } from "./services/firebase";
import { seedSampleJobs } from "./utils/seedData";
import AuthPage   from "./pages/AuthPage";
import KakaoMap   from "./components/KakaoMap";
import PWAManager from "./components/PWAManager";

const CAT_COLORS = { 서비스:"#FF6B35", 행사:"#7C4DFF", 유통:"#00BCD4", 물류:"#FF9800", 요식업:"#E91E63", 홍보:"#4CAF50", 기타:"#9E9E9E" };
const CAT_ICONS  = { 서비스:"☕", 행사:"🎪", 유통:"🏪", 물류:"📦", 요식업:"🍳", 홍보:"📢", 기타:"🔧" };
const CATEGORIES = ["전체", ...Object.keys(CAT_COLORS)];
const CATS = ["서비스","행사","유통","물류","요식업","홍보","기타"];

export default function App() {
  return <AuthProvider><Inner /></AuthProvider>;
}

function Inner() {
  const { user, loading } = useAuth();
  useEffect(() => { seedSampleJobs(); }, []);
  if (loading) return <SplashScreen />;
  return (<><PWAManager />{!user ? <AuthPage onSuccess={() => {}} /> : <MainApp />}</>);
}

function SplashScreen() {
  return (
    <div style={{ minHeight:"100vh", background:"#1a1a2e", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:16 }}>
      <div style={{ fontSize:56 }}>💼</div>
      <div style={{ color:"#FFD60A", fontSize:24, fontWeight:900, fontFamily:"Georgia,serif" }}>하루일거리</div>
      <div style={{ width:40, height:4, background:"#FFD60A", borderRadius:2, animation:"pulse 1s infinite" }} />
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.3}}`}</style>
    </div>
  );
}

function MainApp() {
  const { user, profile, refreshProfile } = useAuth();

  // ★ 로컬 역할 상태 — profile 로드 후 즉시 반영, 전환 시 즉시 UI 변경
  const [localRole, setLocalRole] = useState(null);
  useEffect(() => { if (profile?.role) setLocalRole(profile.role); }, [profile?.role]);
  const isEmployer = (localRole ?? profile?.role) === "employer";

  const [tab, setTab]               = useState("home");
  const [mapVisible, setMapVisible] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobForm, setShowJobForm] = useState(false);
  const [editingJob, setEditingJob]   = useState(null);
  const [toast, setToast]             = useState(null);
  const [applying, setApplying]       = useState(false);
  const [myPage, setMyPage]           = useState(null);

  const { jobs, loading, category, setCategory, keyword, setKeyword, urgentOnly, setUrgentOnly } = useFilteredJobs();

  function notify(msg, type="ok") { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); }

  async function handleApply(job) {
    if (applying) return;
    setApplying(true);
    try { await applyToJob(job.id, user.uid, { name:profile?.name, phone:profile?.phone }); notify(`✅ "${job.title}" 지원 완료!`); setSelectedJob(null); }
    catch (e) { notify(e.message, "error"); }
    finally { setApplying(false); }
  }

  async function handleSwitchRole() {
    const nr = isEmployer ? "worker" : "employer";
    setLocalRole(nr); // 즉시 UI 반영
    try {
      await switchRole(user.uid, nr);
      await refreshProfile();
      notify(nr === "employer" ? "🏢 업체 모드로 전환됐습니다" : "👷 구직자 모드로 전환됐습니다");
    } catch (e) {
      setLocalRole(isEmployer ? "employer" : "worker"); // 실패 시 롤백
      notify("전환 실패: " + e.message, "error");
    }
  }

  const handleMarkerClick = useCallback((job) => { setSelectedJob(job); setMapVisible(false); }, []);

  if (myPage) return (
    <div style={{ maxWidth:390, margin:"0 auto", minHeight:"100vh", background:"#f2f4f7", fontFamily:"'Apple SD Gothic Neo','Noto Sans KR',sans-serif" }}>
      <MySubPage page={myPage} onBack={() => setMyPage(null)} user={user} profile={profile} refreshProfile={refreshProfile} notify={notify} isEmployer={isEmployer} />
    </div>
  );

  return (
    <div style={{ maxWidth:390, margin:"0 auto", minHeight:"100vh", background:"#f2f4f7", fontFamily:"'Apple SD Gothic Neo','Noto Sans KR',sans-serif", position:"relative" }}>
      <style>{`
        @keyframes slideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}
        @keyframes fadeDown{from{opacity:0;transform:translateY(-16px)}to{opacity:1;transform:translateY(0)}}
        @keyframes rolePop{0%{transform:scale(0.8);opacity:0}60%{transform:scale(1.1)}100%{transform:scale(1);opacity:1}}
        ::-webkit-scrollbar{display:none}
        *{-webkit-tap-highlight-color:transparent}
      `}</style>

      {toast && <div style={{ position:"fixed", top:20, left:"50%", transform:"translateX(-50%)", background:toast.type==="error"?"#c62828":"#1a1a2e", color:"#FFD60A", padding:"12px 20px", borderRadius:16, fontWeight:700, zIndex:9999, whiteSpace:"nowrap", animation:"fadeDown 0.3s ease", boxShadow:"0 4px 20px rgba(0,0,0,0.3)" }}>{toast.msg}</div>}

      {mapVisible && (
        <div style={{ position:"fixed", inset:0, zIndex:50, background:"#fff" }}>
          <div style={{ display:"flex", alignItems:"center", padding:"12px 16px", background:"#1a1a2e" }}>
            <button onClick={() => setMapVisible(false)} style={{ background:"transparent", border:"none", color:"#FFD60A", fontSize:20, cursor:"pointer", marginRight:12 }}>←</button>
            <span style={{ color:"#fff", fontWeight:700 }}>지도로 보기</span>
          </div>
          <div style={{ height:"calc(100vh - 56px)" }}><KakaoMap jobs={jobs} onJobSelect={handleMarkerClick} style={{ height:"100%" }} /></div>
        </div>
      )}

      {showJobForm && (
        <div style={{ position:"fixed", inset:0, zIndex:60, background:"#fff", overflowY:"auto" }}>
          <div style={{ display:"flex", alignItems:"center", padding:"12px 16px", background:"#1a1a2e", position:"sticky", top:0 }}>
            <button onClick={() => { setShowJobForm(false); setEditingJob(null); }} style={{ background:"transparent", border:"none", color:"#FFD60A", fontSize:20, cursor:"pointer", marginRight:12 }}>←</button>
            <span style={{ color:"#fff", fontWeight:700 }}>{editingJob ? "공고 수정" : "새 공고 등록"}</span>
          </div>
          <div style={{ padding:"20px 16px" }}>
            <JobFormInline existing={editingJob} employerId={user.uid} employerName={profile?.name??""} onSuccess={() => { setShowJobForm(false); setEditingJob(null); notify(editingJob?"공고 수정 완료":"공고 등록 완료 🎉"); }} onCancel={() => { setShowJobForm(false); setEditingJob(null); }} />
          </div>
        </div>
      )}

      <div style={{ height:"100vh", overflowY:"auto", paddingBottom:80 }}>
        {tab==="home" && <HomeTab jobs={jobs} loading={loading} category={category} setCategory={setCategory} keyword={keyword} setKeyword={setKeyword} urgentOnly={urgentOnly} setUrgentOnly={setUrgentOnly} onSelectJob={setSelectedJob} onShowMap={() => setMapVisible(true)} />}
        {tab==="employer" && <EmployerTab jobs={jobs.filter(j=>j.employerId===user.uid)} onNew={() => setShowJobForm(true)} onEdit={j => { setEditingJob(j); setShowJobForm(true); }} onClose={j => { closeJob(j.id); notify("공고를 마감했습니다"); }} onDelete={j => { if(window.confirm("정말 삭제할까요?")) { deleteJob(j.id); notify("삭제됐습니다"); } }} />}
        {tab==="my" && <MyTab user={user} profile={profile} isEmployer={isEmployer} onMenuClick={setMyPage} onSwitchRole={handleSwitchRole} onLogout={async () => { await logOut(); }} />}
      </div>

      <BottomNav tab={tab} setTab={setTab} />
      {selectedJob && <JobDetailModal job={selectedJob} onClose={() => setSelectedJob(null)} onApply={handleApply} applying={applying} isEmployer={isEmployer} />}
    </div>
  );
}

// ── 홈 탭 ────────────────────────────────────────────────────────
function HomeTab({ jobs, loading, category, setCategory, keyword, setKeyword, urgentOnly, setUrgentOnly, onSelectJob, onShowMap }) {
  return (
    <>
      <div style={{ background:"linear-gradient(135deg,#1a1a2e 0%,#16213e 50%,#0f3460 100%)", padding:"28px 16px 20px", borderRadius:"0 0 28px 28px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <div>
            <div style={{ color:"#FFD60A", fontSize:11, fontWeight:700, letterSpacing:2, fontFamily:"monospace" }}>TODAY'S WORK</div>
            <div style={{ color:"#fff", fontSize:22, fontWeight:900, fontFamily:"Georgia,serif" }}>하루일거리</div>
          </div>
          <button onClick={onShowMap} style={{ background:"rgba(255,255,255,0.15)", border:"none", borderRadius:20, padding:"8px 14px", color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer" }}>🗺️ 지도보기</button>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          {[
            {label:"전체 공고", value:jobs.length+"건", icon:"📋"},
            {label:"급구",      value:jobs.filter(j=>j.urgent).length+"건", icon:"🔥"},
            {label:"평균 시급", value:jobs.length ? Math.round(jobs.reduce((s,j)=>s+j.pay,0)/jobs.length/100)*100+"원":"-", icon:"💰"},
          ].map(s => (
            <div key={s.label} style={{ flex:1, background:"rgba(255,255,255,0.1)", borderRadius:14, padding:"10px 6px", textAlign:"center" }}>
              <div style={{ fontSize:16 }}>{s.icon}</div>
              <div style={{ color:"#FFD60A", fontSize:13, fontWeight:900 }}>{s.value}</div>
              <div style={{ color:"rgba(255,255,255,0.7)", fontSize:10 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ padding:"14px 14px 0" }}>
        <div style={{ display:"flex", alignItems:"center", background:"#fff", borderRadius:14, padding:"10px 14px", marginBottom:12, gap:8, boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
          <span>🔍</span>
          <input value={keyword} onChange={e=>setKeyword(e.target.value)} placeholder="업종, 지역, 회사명 검색..." style={{ border:"none", background:"transparent", flex:1, fontSize:14, outline:"none", fontFamily:"inherit" }} />
          {keyword && <span onClick={()=>setKeyword("")} style={{ cursor:"pointer", color:"#aaa" }}>✕</span>}
        </div>

        <div style={{ display:"flex", gap:8, overflowX:"auto", marginBottom:12, scrollbarWidth:"none" }}>
          {CATEGORIES.map(c => (
            <button key={c} onClick={()=>setCategory(c)} style={{ flexShrink:0, padding:"6px 14px", borderRadius:20, border:"none", background:category===c?"#1a1a2e":"#fff", color:category===c?"#FFD60A":"#666", fontWeight:700, fontSize:12, cursor:"pointer", boxShadow:"0 1px 4px rgba(0,0,0,0.08)" }}>
              {c!=="전체" ? CAT_ICONS[c]+" " : ""}{c}
            </button>
          ))}
        </div>

        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
          <button onClick={()=>setUrgentOnly(!urgentOnly)} style={{ width:42, height:24, borderRadius:12, background:urgentOnly?"#ff4444":"#ddd", border:"none", cursor:"pointer", position:"relative", transition:"background 0.2s" }}>
            <div style={{ position:"absolute", top:3, left:urgentOnly?21:3, width:18, height:18, borderRadius:"50%", background:"#fff", transition:"left 0.2s" }} />
          </button>
          <span style={{ fontSize:13, fontWeight:700, color:urgentOnly?"#ff4444":"#aaa" }}>🔥 급구만 보기</span>
        </div>

        {loading ? (
          <div style={{ textAlign:"center", padding:"40px 0", color:"#aaa" }}><div style={{ fontSize:32 }}>⏳</div><div style={{ marginTop:8 }}>공고 불러오는 중...</div></div>
        ) : jobs.length === 0 ? (
          <div style={{ textAlign:"center", padding:"40px 0", color:"#aaa" }}><div style={{ fontSize:40 }}>🔎</div><div style={{ marginTop:8 }}>공고가 없습니다</div></div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:12, paddingBottom:20 }}>
            {jobs.map(job => <JobCard key={job.id} job={job} onSelect={onSelectJob} />)}
          </div>
        )}
      </div>
    </>
  );
}

// ── 공고 카드 (샘플 뱃지 추가) ──────────────────────────────────
function JobCard({ job, onSelect }) {
  const color = CAT_COLORS[job.category] ?? "#9E9E9E";
  const isSample = job.employerId === "sample";
  return (
    <div onClick={()=>onSelect(job)} style={{ background:"#fff", borderRadius:18, padding:16, boxShadow:"0 2px 12px rgba(0,0,0,0.07)", cursor:"pointer", border:job.urgent?"2px solid #ff4444":"2px solid transparent", position:"relative" }}>
      {/* 급구 뱃지 */}
      {job.urgent && !isSample && <div style={{ position:"absolute", top:-1, right:14, background:"#ff4444", color:"#fff", fontSize:10, fontWeight:900, padding:"2px 10px", borderRadius:"0 0 8px 8px" }}>🔥 급구</div>}
      {/* 샘플 뱃지 */}
      {isSample && <div style={{ position:"absolute", top:-1, right:14, background:"#9E9E9E", color:"#fff", fontSize:10, fontWeight:900, padding:"2px 10px", borderRadius:"0 0 8px 8px" }}>샘플</div>}

      <div style={{ display:"flex", gap:10 }}>
        <div style={{ width:44, height:44, borderRadius:12, background:color+"20", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, flexShrink:0 }}>
          {CAT_ICONS[job.category] ?? "💼"}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <div style={{ fontWeight:800, fontSize:15, color:"#1a1a2e", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{job.title}</div>
            {isSample && <span style={{ background:"#f0f0f0", color:"#999", fontSize:10, padding:"1px 6px", borderRadius:6, fontWeight:700, flexShrink:0 }}>[샘플]</span>}
          </div>
          <div style={{ fontSize:12, color:"#888", marginTop:2 }}>{job.company}</div>
          <div style={{ display:"flex", gap:8, marginTop:6, flexWrap:"wrap" }}>
            {job.tags?.slice(0,2).map(t => <span key={t} style={{ background:color+"20", color, fontSize:11, padding:"2px 8px", borderRadius:6, fontWeight:600 }}>#{t}</span>)}
          </div>
          <div style={{ display:"flex", gap:12, marginTop:6 }}>
            <span style={{ fontSize:11, color:"#888" }}>📍 {job.location}</span>
            <span style={{ fontSize:11, color:"#888" }}>⏰ {job.startTime} · {job.duration}h</span>
          </div>
        </div>
        <div style={{ textAlign:"right", flexShrink:0 }}>
          <div style={{ color:"#1a1a2e", fontWeight:900, fontSize:16 }}>{job.pay?.toLocaleString()}</div>
          <div style={{ fontSize:10, color:"#aaa" }}>원/시간</div>
          <div style={{ marginTop:6, fontSize:12, color:"#2e7d32", fontWeight:700 }}>총 {(job.pay*job.duration).toLocaleString()}원</div>
          <div style={{ fontSize:11, color:(job.slots-(job.filledSlots??0))<=1?"#ff4444":"#888" }}>잔여 {job.slots-(job.filledSlots??0)}자리</div>
        </div>
      </div>
    </div>
  );
}

// ── 공고 상세 모달 ───────────────────────────────────────────────
function JobDetailModal({ job, onClose, onApply, applying, isEmployer }) {
  const color = CAT_COLORS[job.category] ?? "#9E9E9E";
  const isSample = job.employerId === "sample";
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:200, display:"flex", alignItems:"flex-end", backdropFilter:"blur(4px)" }}>
      <div style={{ background:"#fff", borderRadius:"24px 24px 0 0", width:"100%", maxHeight:"88vh", overflowY:"auto", animation:"slideUp 0.3s ease" }}>
        <div style={{ position:"sticky", top:0, background:"#fff", padding:"14px 20px 0", borderRadius:"24px 24px 0 0" }}>
          <div style={{ width:40, height:4, background:"#e0e0e0", borderRadius:2, margin:"0 auto 16px" }} />
        </div>
        <div style={{ padding:"0 20px 32px" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              <span style={{ fontSize:28 }}>{CAT_ICONS[job.category] ?? "💼"}</span>
              <div>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <div style={{ fontWeight:900, fontSize:20, color:"#1a1a2e" }}>{job.title}</div>
                  {isSample && <span style={{ background:"#e0e0e0", color:"#777", fontSize:11, padding:"2px 8px", borderRadius:8, fontWeight:700 }}>[샘플]</span>}
                </div>
                <div style={{ color:"#888", fontSize:13 }}>{job.company}</div>
              </div>
            </div>
            {job.urgent && !isSample && <span style={{ background:"#ff4444", color:"#fff", fontSize:11, padding:"4px 10px", borderRadius:10, fontWeight:700 }}>🔥 급구</span>}
          </div>

          {isSample && (
            <div style={{ background:"#f5f5f5", borderRadius:12, padding:"10px 14px", marginBottom:16, fontSize:12, color:"#888", display:"flex", gap:8, alignItems:"center" }}>
              <span>ℹ️</span> 이 공고는 앱 체험을 위한 샘플입니다. 실제 채용과 무관합니다.
            </div>
          )}

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:18 }}>
            {[
              {label:"시급", value:job.pay?.toLocaleString()+"원", highlight:true},
              {label:"예상 총 수입", value:(job.pay*job.duration).toLocaleString()+"원", highlight:true},
              {label:"근무 시간", value:job.duration+"시간"},
              {label:"시작 시간", value:job.startTime},
              {label:"날짜", value:job.startDate||"미정"},
              {label:"잔여 자리", value:(job.slots-(job.filledSlots??0))+"자리", danger:(job.slots-(job.filledSlots??0))<=1},
            ].map(item => (
              <div key={item.label} style={{ background:item.highlight?"#1a1a2e":item.danger?"#fff3f3":"#f8f8f8", borderRadius:12, padding:12 }}>
                <div style={{ fontSize:11, color:item.highlight?"rgba(255,255,255,0.65)":item.danger?"#cc0000":"#888" }}>{item.label}</div>
                <div style={{ fontSize:15, fontWeight:900, marginTop:3, color:item.highlight?"#FFD60A":item.danger?"#cc0000":"#1a1a2e" }}>{item.value}</div>
              </div>
            ))}
          </div>

          {job.address && (
            <div style={{ background:"#f8f8f8", borderRadius:14, padding:"12px 14px", marginBottom:14, display:"flex", gap:10, alignItems:"center" }}>
              <span style={{ fontSize:20 }}>📍</span>
              <div><div style={{ fontSize:11, color:"#888" }}>근무 위치</div><div style={{ fontWeight:700, fontSize:14 }}>{job.address}</div></div>
            </div>
          )}
          {job.detail && (
            <div style={{ background:"#f8f8f8", borderRadius:14, padding:14, marginBottom:14 }}>
              <div style={{ fontWeight:700, marginBottom:8, fontSize:14 }}>📝 업무 내용</div>
              <div style={{ fontSize:13, color:"#555", lineHeight:1.7 }}>{job.detail}</div>
            </div>
          )}
          {job.tags?.length > 0 && (
            <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:20 }}>
              {job.tags.map(t => <span key={t} style={{ background:color+"20", color, fontSize:12, padding:"4px 10px", borderRadius:8, fontWeight:600 }}>#{t}</span>)}
            </div>
          )}

          <div style={{ display:"flex", gap:10 }}>
            <button onClick={onClose} style={{ flex:1, background:"#f0f0f0", border:"none", borderRadius:14, padding:16, fontWeight:700, fontSize:15, cursor:"pointer", color:"#555" }}>닫기</button>
            {!isEmployer && (
              <button onClick={()=>onApply(job)} disabled={applying} style={{ flex:2, background:applying?"#aaa":"linear-gradient(90deg,#1a1a2e,#0f3460)", border:"none", borderRadius:14, padding:16, color:"#FFD60A", fontWeight:900, fontSize:15, cursor:applying?"not-allowed":"pointer" }}>
                {applying ? "지원 중..." : isSample ? "샘플 지원 체험" : "바로 지원하기 →"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── 업체 탭 ──────────────────────────────────────────────────────
function EmployerTab({ jobs, onNew, onEdit, onClose, onDelete }) {
  return (
    <div>
      <div style={{ background:"linear-gradient(135deg,#7C4DFF,#448aff)", padding:"28px 16px 24px" }}>
        <div style={{ color:"#FFD60A", fontSize:11, fontWeight:700, letterSpacing:2 }}>EMPLOYER</div>
        <div style={{ color:"#fff", fontSize:22, fontWeight:900, fontFamily:"Georgia,serif", marginTop:4 }}>공고 관리</div>
        <div style={{ display:"flex", gap:8, marginTop:14 }}>
          {[{label:"등록 공고",v:jobs.length+"건"},{label:"진행중",v:jobs.filter(j=>j.status==="open").length+"건"},{label:"총 지원자",v:jobs.reduce((s,j)=>s+(j.applyCount??0),0)+"명"}].map(s => (
            <div key={s.label} style={{ flex:1, background:"rgba(255,255,255,0.15)", borderRadius:12, padding:"10px 8px", textAlign:"center" }}>
              <div style={{ color:"#fff", fontWeight:900, fontSize:16 }}>{s.v}</div>
              <div style={{ color:"rgba(255,255,255,0.8)", fontSize:10 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ padding:16 }}>
        <button onClick={onNew} style={{ width:"100%", background:"linear-gradient(90deg,#7C4DFF,#448aff)", border:"none", borderRadius:16, padding:16, color:"#fff", fontWeight:900, fontSize:16, cursor:"pointer", marginBottom:20, fontFamily:"inherit" }}>＋ 새 공고 등록</button>
        {jobs.length === 0 ? (
          <div style={{ textAlign:"center", padding:"40px 0", color:"#aaa" }}>
            <div style={{ fontSize:40 }}>📭</div>
            <div style={{ marginTop:8 }}>등록한 공고가 없습니다</div>
            <div style={{ fontSize:12, marginTop:6, color:"#bbb" }}>위 버튼으로 첫 공고를 등록해보세요</div>
          </div>
        ) : jobs.map(job => (
          <div key={job.id} style={{ background:"#fff", borderRadius:16, padding:16, marginBottom:12, boxShadow:"0 2px 10px rgba(0,0,0,0.07)" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:800, fontSize:15 }}>{job.title}</div>
                <div style={{ color:"#888", fontSize:12, marginTop:2 }}>{job.startDate} {job.startTime} · {job.duration}h</div>
              </div>
              <span style={{ background:job.status==="open"?"#e8f5e9":"#f5f5f5", color:job.status==="open"?"#2e7d32":"#888", fontSize:11, padding:"4px 10px", borderRadius:10, fontWeight:700 }}>
                {job.status==="open"?"진행중":job.status==="closed"?"마감":"취소"}
              </span>
            </div>
            <div style={{ display:"flex", gap:8, marginTop:12 }}>
              <div style={{ flex:1, background:"#f8f8f8", borderRadius:10, padding:8, textAlign:"center" }}><div style={{ fontWeight:900, fontSize:18, color:"#7C4DFF" }}>{job.applyCount??0}</div><div style={{ fontSize:10, color:"#888" }}>지원자</div></div>
              <div style={{ flex:1, background:"#f8f8f8", borderRadius:10, padding:8, textAlign:"center" }}><div style={{ fontWeight:900, fontSize:18, color:"#FF9800" }}>{job.viewCount??0}</div><div style={{ fontSize:10, color:"#888" }}>조회수</div></div>
              {job.status==="open" && <>
                <button onClick={()=>onEdit(job)} style={{ flex:1, background:"#1a1a2e", border:"none", borderRadius:10, color:"#FFD60A", fontWeight:700, fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>수정</button>
                <button onClick={()=>onClose(job)} style={{ flex:1, background:"#ff9800", border:"none", borderRadius:10, color:"#fff", fontWeight:700, fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>마감</button>
              </>}
              <button onClick={()=>onDelete(job)} style={{ flex:1, background:"#ffebee", border:"none", borderRadius:10, color:"#c62828", fontWeight:700, fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>삭제</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── 공고 등록 폼 ─────────────────────────────────────────────────
function JobFormInline({ existing, employerId, employerName, onSuccess, onCancel }) {
  const [form, setForm] = useState(existing ? {
    ...existing, pay:String(existing.pay), duration:String(existing.duration), slots:String(existing.slots), tags:existing.tags?.join(", ")
  } : { title:"", category:"서비스", pay:"", duration:"", slots:"1", startDate:"", startTime:"", address:"", location:"", detail:"", tags:"", urgent:false });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  function validate() {
    const e={};
    if(!form.title.trim()) e.title="필수 입력";
    if(!form.pay||Number(form.pay)<9860) e.pay="최저임금 이상";
    if(!form.duration||Number(form.duration)<1) e.duration="필수 입력";
    if(!form.startDate) e.startDate="필수 입력";
    if(!form.startTime) e.startTime="필수 입력";
    if(!form.address.trim()) e.address="필수 입력";
    setErrors(e); return Object.keys(e).length===0;
  }

  async function handleSubmit() {
    if(!validate()) return;
    setSaving(true);
    try {
      const payload = { ...form, pay:Number(form.pay), duration:Number(form.duration), slots:Number(form.slots), tags:form.tags.split(",").map(t=>t.trim()).filter(Boolean), lat:null, lng:null };
      delete payload.id;
      if(existing) await updateJob(existing.id, payload);
      else await createJob(payload, employerId, employerName);
      onSuccess();
    } catch(e) { alert("저장 실패: "+e.message); }
    finally { setSaving(false); }
  }

  const inp = (err) => ({ width:"100%", boxSizing:"border-box", border:`2px solid ${err?"#cc0000":"#eee"}`, borderRadius:10, padding:"10px 12px", fontSize:14, fontFamily:"inherit", outline:"none", background:"#fff" });
  const lbl = (err, text) => <div style={{ fontSize:12, fontWeight:700, color:err?"#cc0000":"#555", marginBottom:6 }}>{text}{err&&<span style={{ fontWeight:400 }}> — {err}</span>}</div>;

  return (
    <div>
      <div style={{ marginBottom:16 }}>
        <div style={{ fontSize:12, fontWeight:700, color:"#555", marginBottom:8 }}>카테고리</div>
        <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
          {CATS.map(c => <button key={c} onClick={()=>set("category",c)} style={{ padding:"6px 14px", borderRadius:20, border:"none", background:form.category===c?"#1a1a2e":"#f0f0f0", color:form.category===c?"#FFD60A":"#555", fontWeight:700, fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>{CAT_ICONS[c]} {c}</button>)}
        </div>
      </div>
      <div style={{ marginBottom:14 }}>{lbl(errors.title,"공고 제목 *")}<input value={form.title} onChange={e=>set("title",e.target.value)} placeholder="예: 카페 홀 서빙" style={inp(errors.title)} /></div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
        <div>{lbl(errors.pay,"시급 *")}<input type="number" value={form.pay} onChange={e=>set("pay",e.target.value)} placeholder="12000" style={inp(errors.pay)} /></div>
        <div>{lbl(errors.duration,"근무 시간 *")}<input type="number" value={form.duration} onChange={e=>set("duration",e.target.value)} placeholder="4" style={inp(errors.duration)} /></div>
        <div>{lbl(errors.startDate,"날짜 *")}<input type="date" value={form.startDate} onChange={e=>set("startDate",e.target.value)} style={inp(errors.startDate)} /></div>
        <div>{lbl(errors.startTime,"시작 시간 *")}<input type="time" value={form.startTime} onChange={e=>set("startTime",e.target.value)} style={inp(errors.startTime)} /></div>
        <div>{lbl(null,"모집 인원")}<input type="number" value={form.slots} min="1" onChange={e=>set("slots",e.target.value)} style={inp(false)} /></div>
        <div>{lbl(null,"급구 여부")}
          <div style={{ display:"flex", alignItems:"center", gap:10, height:42 }}>
            <button onClick={()=>set("urgent",!form.urgent)} style={{ width:48, height:26, borderRadius:13, background:form.urgent?"#ff4444":"#ddd", border:"none", cursor:"pointer", position:"relative", transition:"background 0.2s", fontFamily:"inherit" }}>
              <div style={{ position:"absolute", top:3, left:form.urgent?25:3, width:20, height:20, borderRadius:"50%", background:"#fff", transition:"left 0.2s" }} />
            </button>
            <span style={{ fontSize:13, color:form.urgent?"#ff4444":"#888", fontWeight:700 }}>{form.urgent?"🔥 급구":"일반"}</span>
          </div>
        </div>
      </div>
      <div style={{ marginBottom:14 }}>{lbl(errors.address,"주소 *")}<input value={form.address} onChange={e=>set("address",e.target.value)} placeholder="서울 강남구 테헤란로 123" style={inp(errors.address)} /></div>
      <div style={{ marginBottom:14 }}>{lbl(null,"지역명")}<input value={form.location} onChange={e=>set("location",e.target.value)} placeholder="예: 강남구" style={inp(false)} /></div>
      <div style={{ marginBottom:14 }}>{lbl(null,"태그 (쉼표 구분)")}<input value={form.tags} onChange={e=>set("tags",e.target.value)} placeholder="서빙, 야간, 단기" style={inp(false)} /></div>
      <div style={{ marginBottom:20 }}>{lbl(null,"업무 설명")}<textarea value={form.detail} onChange={e=>set("detail",e.target.value)} rows={4} placeholder="업무 내용, 복장, 주의사항 등" style={{ ...inp(false), resize:"vertical", lineHeight:1.6 }} /></div>
      {form.pay&&form.duration && <div style={{ background:"#1a1a2e", borderRadius:14, padding:"12px 16px", marginBottom:20, display:"flex", justifyContent:"space-between" }}><span style={{ color:"rgba(255,255,255,0.7)", fontSize:13 }}>예상 수입</span><span style={{ color:"#FFD60A", fontWeight:900, fontSize:16 }}>{(Number(form.pay)*Number(form.duration)).toLocaleString()}원</span></div>}
      <div style={{ display:"flex", gap:10 }}>
        <button onClick={onCancel} style={{ flex:1, background:"#f0f0f0", border:"none", borderRadius:14, padding:16, fontWeight:700, fontSize:15, cursor:"pointer", color:"#555", fontFamily:"inherit" }}>취소</button>
        <button onClick={handleSubmit} disabled={saving} style={{ flex:2, background:saving?"#888":"linear-gradient(90deg,#1a1a2e,#0f3460)", border:"none", borderRadius:14, padding:16, color:"#FFD60A", fontWeight:900, fontSize:15, cursor:saving?"not-allowed":"pointer", fontFamily:"inherit" }}>
          {saving?"저장 중...":existing?"수정 완료":"공고 등록하기 →"}
        </button>
      </div>
    </div>
  );
}

// ── MY 탭 — 역할별 메뉴 분기 ─────────────────────────────────────
function MyTab({ user, profile, isEmployer, onMenuClick, onSwitchRole, onLogout }) {
  // 구직자 메뉴
  const workerMenus = [
    {id:"applications", icon:"📋", label:"내 지원 내역"},
    {id:"earnings",     icon:"💰", label:"수입 정산 내역"},
    {id:"reviews",      icon:"⭐", label:"받은 평가"},
    {id:"notification", icon:"🔔", label:"알림 설정"},
    {id:"account",      icon:"🏦", label:"계좌 정보"},
    {id:"profile",      icon:"👤", label:"프로필 수정"},
    {id:"cs",           icon:"📞", label:"고객센터"},
  ];
  // 업체 메뉴
  const employerMenus = [
    {id:"myJobs",       icon:"📋", label:"내 공고 현황"},
    {id:"applicants",   icon:"👥", label:"지원자 관리"},
    {id:"settlement",   icon:"💳", label:"정산 관리"},
    {id:"notification", icon:"🔔", label:"알림 설정"},
    {id:"bizProfile",   icon:"🏢", label:"업체 정보 수정"},
    {id:"profile",      icon:"👤", label:"계정 설정"},
    {id:"cs",           icon:"📞", label:"고객센터"},
  ];

  const menus = isEmployer ? employerMenus : workerMenus;

  return (
    <div>
      {/* 헤더 */}
      <div style={{ background: isEmployer ? "linear-gradient(135deg,#7C4DFF,#448aff)" : "linear-gradient(135deg,#1a1a2e,#0f3460)", padding:"28px 16px 24px", transition:"background 0.4s" }}>
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          {/* 아바타 */}
          <div style={{ width:60, height:60, borderRadius:"50%", background:"#FFD60A", display:"flex", alignItems:"center", justifyContent:"center", fontSize:28, boxShadow: isEmployer?"0 0 0 3px rgba(255,255,255,0.4)":"none", transition:"box-shadow 0.3s" }}>
            {isEmployer ? "🏢" : (profile?.name?.[0] ?? "😊")}
          </div>
          <div>
            <div style={{ color:"#fff", fontWeight:900, fontSize:20 }}>{profile?.name ?? user.displayName ?? "사용자"}</div>
            <div style={{ color:"rgba(255,255,255,0.7)", fontSize:13 }}>{user.email}</div>
            {/* 역할 배지 — 전환 시 애니메이션 */}
            <div key={isEmployer?"emp":"wkr"} style={{ display:"inline-flex", alignItems:"center", gap:4, marginTop:4, background:"rgba(255,255,255,0.2)", color:"#fff", fontSize:12, fontWeight:700, padding:"3px 12px", borderRadius:20, animation:"rolePop 0.35s ease" }}>
              {isEmployer ? "🏢 업체" : "👷 구직자"}
            </div>
          </div>
        </div>

        {/* 역할 전환 버튼 */}
        <button onClick={onSwitchRole} style={{ marginTop:14, width:"100%", background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.3)", borderRadius:12, padding:10, color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
          🔄 {isEmployer ? "👷 구직자 모드로 전환" : "🏢 업체 모드로 전환"}
        </button>

        {/* 업체 모드 안내 */}
        {isEmployer && (
          <div style={{ marginTop:10, background:"rgba(255,255,255,0.1)", borderRadius:10, padding:"8px 12px", fontSize:11, color:"rgba(255,255,255,0.8)" }}>
            💡 업체 모드: 공고를 등록하고 지원자를 관리할 수 있습니다
          </div>
        )}
      </div>

      {/* 메뉴 목록 */}
      <div style={{ padding:16 }}>
        {/* 업체 모드 — 빠른 접근 카드 */}
        {isEmployer && (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
            {[
              {label:"오늘 조회수", value:"0", icon:"👁️", color:"#7C4DFF"},
              {label:"총 지원자", value:"0", icon:"👥", color:"#448aff"},
              {label:"진행중 공고", value:"0", icon:"📋", color:"#FF9800"},
              {label:"이번달 채용", value:"0", icon:"✅", color:"#4CAF50"},
            ].map(s => (
              <div key={s.label} style={{ background:"#fff", borderRadius:14, padding:"14px 12px", textAlign:"center", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
                <div style={{ fontSize:22 }}>{s.icon}</div>
                <div style={{ fontWeight:900, fontSize:20, color:s.color, marginTop:4 }}>{s.value}</div>
                <div style={{ fontSize:11, color:"#888", marginTop:2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {menus.map(item => (
          <div key={item.id} onClick={() => onMenuClick(item.id)} style={{ background:"#fff", borderRadius:12, padding:"14px 16px", marginBottom:8, display:"flex", justifyContent:"space-between", alignItems:"center", boxShadow:"0 1px 4px rgba(0,0,0,0.05)", cursor:"pointer" }}>
            <div style={{ display:"flex", gap:12, alignItems:"center" }}>
              <span style={{ fontSize:18 }}>{item.icon}</span>
              <span style={{ fontSize:14, color:"#333" }}>{item.label}</span>
            </div>
            <span style={{ color:"#ccc", fontSize:18 }}>›</span>
          </div>
        ))}

        <button onClick={onLogout} style={{ width:"100%", background:"#ffebee", border:"none", borderRadius:14, padding:16, color:"#c62828", fontWeight:700, fontSize:15, cursor:"pointer", marginTop:8, fontFamily:"inherit" }}>
          로그아웃
        </button>
      </div>
    </div>
  );
}

// ── MY 서브페이지 ────────────────────────────────────────────────
function MySubPage({ page, onBack, user, profile, refreshProfile, notify, isEmployer }) {
  const titles = {
    applications:"내 지원 내역", earnings:"수입 정산 내역", reviews:"받은 평가",
    notification:"알림 설정",   account:"계좌 정보",        profile:"프로필 수정",
    cs:"고객센터",              myJobs:"내 공고 현황",       applicants:"지원자 관리",
    settlement:"정산 관리",     bizProfile:"업체 정보 수정",
  };
  const bgColor = isEmployer ? "#7C4DFF" : "#1a1a2e";

  return (
    <div style={{ minHeight:"100vh", background:"#f2f4f7", fontFamily:"'Apple SD Gothic Neo','Noto Sans KR',sans-serif" }}>
      <div style={{ display:"flex", alignItems:"center", padding:"12px 16px", background:bgColor, position:"sticky", top:0, zIndex:10 }}>
        <button onClick={onBack} style={{ background:"transparent", border:"none", color:"#FFD60A", fontSize:20, cursor:"pointer", marginRight:12 }}>←</button>
        <span style={{ color:"#fff", fontWeight:700, fontSize:16 }}>{titles[page] ?? page}</span>
      </div>
      <div style={{ padding:16 }}>
        {page==="applications" && <ApplicationsPage />}
        {page==="earnings"     && <EarningsPage />}
        {page==="reviews"      && <ReviewsPage />}
        {page==="notification" && <NotificationPage />}
        {page==="account"      && <AccountPage />}
        {page==="profile"      && <ProfilePage user={user} profile={profile} refreshProfile={refreshProfile} notify={notify} />}
        {page==="cs"           && <CSPage />}
        {page==="myJobs"       && <MyJobsPage />}
        {page==="applicants"   && <ApplicantsPage />}
        {page==="settlement"   && <SettlementPage />}
        {page==="bizProfile"   && <BizProfilePage profile={profile} user={user} refreshProfile={refreshProfile} notify={notify} />}
      </div>
    </div>
  );
}

// ── 서브페이지 컴포넌트들 ────────────────────────────────────────

function ApplicationsPage() {
  return <div style={{ background:"#fff", borderRadius:16, padding:24, textAlign:"center", color:"#aaa" }}><div style={{ fontSize:48, marginBottom:12 }}>📋</div><div style={{ fontWeight:700, color:"#333", marginBottom:6 }}>지원 내역이 없습니다</div><div style={{ fontSize:13 }}>공고에 지원하면 여기에 표시됩니다</div></div>;
}

function EarningsPage() {
  return (
    <div>
      <div style={{ background:"linear-gradient(135deg,#1a1a2e,#0f3460)", borderRadius:16, padding:20, marginBottom:16, color:"#fff" }}>
        <div style={{ fontSize:13, color:"rgba(255,255,255,0.7)" }}>이번달 총 수입</div>
        <div style={{ fontSize:32, fontWeight:900, color:"#FFD60A", marginTop:4 }}>₩ 0</div>
        <div style={{ fontSize:12, color:"rgba(255,255,255,0.5)", marginTop:4 }}>근무 완료 후 자동 정산됩니다</div>
      </div>
      <div style={{ background:"#fff", borderRadius:16, padding:24, textAlign:"center", color:"#aaa" }}><div style={{ fontSize:48, marginBottom:12 }}>💰</div><div style={{ fontSize:13 }}>정산 내역이 없습니다</div></div>
    </div>
  );
}

function ReviewsPage() {
  return (
    <div>
      <div style={{ background:"#fff", borderRadius:16, padding:20, marginBottom:12, display:"flex", alignItems:"center", gap:16 }}>
        <div style={{ textAlign:"center" }}><div style={{ fontSize:36, fontWeight:900, color:"#FFD60A" }}>-</div><div style={{ fontSize:11, color:"#888" }}>평균 별점</div></div>
        <div style={{ flex:1 }}>{[5,4,3,2,1].map(n => <div key={n} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}><span style={{ fontSize:12, color:"#888", width:12 }}>{n}</span><div style={{ flex:1, height:6, background:"#f0f0f0", borderRadius:3 }} /><span style={{ fontSize:11, color:"#888", width:20 }}>0</span></div>)}</div>
      </div>
      <div style={{ background:"#fff", borderRadius:16, padding:24, textAlign:"center", color:"#aaa" }}><div style={{ fontSize:48, marginBottom:12 }}>⭐</div><div style={{ fontSize:13 }}>아직 받은 평가가 없습니다</div></div>
    </div>
  );
}

function NotificationPage() {
  const [s, setS] = useState({ urgent:true, apply:true, chat:false, marketing:false });
  const toggle = k => setS(prev=>({...prev,[k]:!prev[k]}));
  const items = [{key:"urgent",label:"급구 공고 알림",desc:"내 주변 급구 공고 등록 시"},{key:"apply",label:"지원 결과 알림",desc:"합격/불합격 결과"},{key:"chat",label:"채팅 알림",desc:"업체 메시지"},{key:"marketing",label:"마케팅 알림",desc:"이벤트·프로모션"}];
  return <div>{items.map(item => <div key={item.key} style={{ background:"#fff", borderRadius:14, padding:16, marginBottom:10, display:"flex", justifyContent:"space-between", alignItems:"center", boxShadow:"0 1px 4px rgba(0,0,0,0.05)" }}><div><div style={{ fontWeight:700, fontSize:14, color:"#1a1a2e" }}>{item.label}</div><div style={{ fontSize:12, color:"#888", marginTop:2 }}>{item.desc}</div></div><button onClick={()=>toggle(item.key)} style={{ width:48, height:26, borderRadius:13, background:s[item.key]?"#1a1a2e":"#ddd", border:"none", cursor:"pointer", position:"relative", transition:"background 0.2s", flexShrink:0, fontFamily:"inherit" }}><div style={{ position:"absolute", top:3, left:s[item.key]?25:3, width:20, height:20, borderRadius:"50%", background:"#fff", transition:"left 0.2s" }} /></button></div>)}</div>;
}

function AccountPage() {
  const [bank,setBank]=useState(""); const [num,setNum]=useState(""); const [name,setName]=useState("");
  const inp={width:"100%",boxSizing:"border-box",border:"2px solid #eee",borderRadius:10,padding:"10px 12px",fontSize:14,fontFamily:"inherit",outline:"none"};
  return (
    <div>
      <div style={{ background:"#fff", borderRadius:16, padding:20, marginBottom:16 }}>
        <div style={{ fontWeight:900, fontSize:15, color:"#1a1a2e", marginBottom:16 }}>정산 계좌 등록</div>
        <div style={{ fontSize:12, fontWeight:700, color:"#555", marginBottom:6 }}>은행</div>
        <select value={bank} onChange={e=>setBank(e.target.value)} style={{ ...inp, marginBottom:12 }}>
          <option value="">은행 선택</option>
          {["KB국민","신한","하나","우리","IBK기업","NH농협","카카오뱅크","토스뱅크","케이뱅크"].map(b=><option key={b} value={b}>{b}은행</option>)}
        </select>
        <div style={{ fontSize:12, fontWeight:700, color:"#555", marginBottom:6 }}>계좌번호</div>
        <input value={num} onChange={e=>setNum(e.target.value)} placeholder="- 없이 숫자만" style={{ ...inp, marginBottom:12 }} type="tel" />
        <div style={{ fontSize:12, fontWeight:700, color:"#555", marginBottom:6 }}>예금주</div>
        <input value={name} onChange={e=>setName(e.target.value)} placeholder="예금주 이름" style={{ ...inp, marginBottom:20 }} />
        <button style={{ width:"100%", background:"#1a1a2e", border:"none", borderRadius:14, padding:14, color:"#FFD60A", fontWeight:900, fontSize:15, cursor:"pointer", fontFamily:"inherit" }}>계좌 등록하기</button>
      </div>
      <div style={{ background:"#fff3cd", borderRadius:12, padding:"12px 16px", fontSize:13, color:"#856404" }}>⚠️ 본인 명의 계좌만 등록 가능합니다.</div>
    </div>
  );
}

// ── 프로필 수정 + 비밀번호 변경 ─────────────────────────────────
function ProfilePage({ user, profile, refreshProfile, notify }) {
  const [name, setName]     = useState(profile?.name ?? "");
  const [phone, setPhone]   = useState(profile?.phone ?? "");
  const [saving, setSaving] = useState(false);
  // 비밀번호 변경
  const [curPw, setCurPw]   = useState("");
  const [newPw, setNewPw]   = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const inp = { width:"100%", boxSizing:"border-box", border:"2px solid #eee", borderRadius:10, padding:"10px 12px", fontSize:14, fontFamily:"inherit", outline:"none" };

  async function handleSaveProfile() {
    setSaving(true);
    try { await updateUserProfile(user.uid, { name, phone }); await refreshProfile(); notify("✅ 프로필이 수정됐습니다"); }
    catch (e) { notify("저장 실패: "+e.message, "error"); }
    finally { setSaving(false); }
  }

  async function handleChangePassword() {
    if (!curPw) return notify("현재 비밀번호를 입력하세요", "error");
    if (newPw.length < 6) return notify("새 비밀번호는 6자 이상이어야 합니다", "error");
    if (newPw !== newPw2) return notify("새 비밀번호가 일치하지 않습니다", "error");
    setPwSaving(true);
    try {
      // 재인증
      const credential = EmailAuthProvider.credential(user.email, curPw);
      await reauthenticateWithCredential(auth.currentUser, credential);
      // 비밀번호 변경
      await updatePassword(auth.currentUser, newPw);
      notify("✅ 비밀번호가 변경됐습니다");
      setCurPw(""); setNewPw(""); setNewPw2("");
    } catch (e) {
      const msgs = { "auth/wrong-password":"현재 비밀번호가 틀렸습니다.", "auth/too-many-requests":"잠시 후 다시 시도해 주세요." };
      notify(msgs[e.code] ?? e.message, "error");
    } finally { setPwSaving(false); }
  }

  return (
    <div>
      {/* 프로필 수정 */}
      <div style={{ background:"#fff", borderRadius:16, padding:20, marginBottom:16 }}>
        <div style={{ fontWeight:900, fontSize:15, color:"#1a1a2e", marginBottom:16 }}>기본 정보</div>
        <div style={{ textAlign:"center", marginBottom:20 }}>
          <div style={{ width:72, height:72, borderRadius:"50%", background:"#FFD60A", display:"flex", alignItems:"center", justifyContent:"center", fontSize:32, margin:"0 auto 8px" }}>{name?.[0] ?? "😊"}</div>
          <div style={{ fontSize:12, color:"#888" }}>{user.email}</div>
        </div>
        <div style={{ fontSize:12, fontWeight:700, color:"#555", marginBottom:6 }}>이름</div>
        <input value={name} onChange={e=>setName(e.target.value)} placeholder="이름 입력" style={{ ...inp, marginBottom:14 }} />
        <div style={{ fontSize:12, fontWeight:700, color:"#555", marginBottom:6 }}>연락처</div>
        <input value={phone} onChange={e=>setPhone(e.target.value)} placeholder="010-0000-0000" type="tel" style={{ ...inp, marginBottom:20 }} />
        <button onClick={handleSaveProfile} disabled={saving} style={{ width:"100%", background:saving?"#aaa":"#1a1a2e", border:"none", borderRadius:14, padding:14, color:"#FFD60A", fontWeight:900, fontSize:15, cursor:"pointer", fontFamily:"inherit" }}>
          {saving ? "저장 중..." : "프로필 저장"}
        </button>
      </div>

      {/* 비밀번호 변경 */}
      <div style={{ background:"#fff", borderRadius:16, padding:20 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:showPw?16:0 }}>
          <div style={{ fontWeight:900, fontSize:15, color:"#1a1a2e" }}>🔒 비밀번호 변경</div>
          <button onClick={()=>setShowPw(!showPw)} style={{ background:"#f0f0f0", border:"none", borderRadius:10, padding:"6px 14px", fontSize:13, fontWeight:700, cursor:"pointer", color:"#555", fontFamily:"inherit" }}>
            {showPw ? "접기" : "변경하기"}
          </button>
        </div>

        {showPw && (
          <>
            <div style={{ fontSize:12, fontWeight:700, color:"#555", marginBottom:6 }}>현재 비밀번호</div>
            <input type="password" value={curPw} onChange={e=>setCurPw(e.target.value)} placeholder="현재 비밀번호 입력" style={{ ...inp, marginBottom:14 }} />

            <div style={{ fontSize:12, fontWeight:700, color:"#555", marginBottom:6 }}>새 비밀번호</div>
            <input type="password" value={newPw} onChange={e=>setNewPw(e.target.value)} placeholder="새 비밀번호 (6자 이상)" style={{ ...inp, marginBottom:14 }} />

            <div style={{ fontSize:12, fontWeight:700, color:"#555", marginBottom:6 }}>새 비밀번호 확인</div>
            <input type="password" value={newPw2} onChange={e=>setNewPw2(e.target.value)} placeholder="새 비밀번호 재입력" style={{ ...inp, marginBottom:6 }} />

            {/* 일치 여부 표시 */}
            {newPw && newPw2 && (
              <div style={{ fontSize:12, marginBottom:14, color:newPw===newPw2?"#2e7d32":"#cc0000", fontWeight:600 }}>
                {newPw===newPw2 ? "✅ 비밀번호가 일치합니다" : "⚠️ 비밀번호가 일치하지 않습니다"}
              </div>
            )}

            <button onClick={handleChangePassword} disabled={pwSaving} style={{ width:"100%", background:pwSaving?"#aaa":"linear-gradient(90deg,#7C4DFF,#448aff)", border:"none", borderRadius:14, padding:14, color:"#fff", fontWeight:900, fontSize:15, cursor:"pointer", fontFamily:"inherit", marginTop:4 }}>
              {pwSaving ? "변경 중..." : "비밀번호 변경"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function CSPage() {
  return (
    <div>
      {[
        {icon:"💬", title:"1:1 채팅 문의", desc:"평일 09:00~18:00"},
        {icon:"📧", title:"이메일 문의", desc:"privacy@haruwork.app"},
        {icon:"❓", title:"자주 묻는 질문", desc:"FAQ 바로 보기"},
        {icon:"📋", title:"이용약관", desc:"서비스 이용약관"},
        {icon:"🔒", title:"개인정보처리방침", desc:"haru-ilgeori.web.app/privacy"},
      ].map(item => (
        <div key={item.title} onClick={()=>{ if(item.title==="이메일 문의")window.location.href="mailto:privacy@haruwork.app"; if(item.title==="개인정보처리방침")window.open("https://haru-ilgeori.web.app/privacy"); }}
          style={{ background:"#fff", borderRadius:14, padding:16, marginBottom:10, display:"flex", alignItems:"center", gap:14, boxShadow:"0 1px 4px rgba(0,0,0,0.05)", cursor:"pointer" }}>
          <span style={{ fontSize:24 }}>{item.icon}</span>
          <div style={{ flex:1 }}><div style={{ fontWeight:700, fontSize:14, color:"#1a1a2e" }}>{item.title}</div><div style={{ fontSize:12, color:"#888", marginTop:2 }}>{item.desc}</div></div>
          <span style={{ color:"#ccc", fontSize:18 }}>›</span>
        </div>
      ))}
      <div style={{ background:"#f8f8f8", borderRadius:14, padding:"14px 16px", marginTop:8, fontSize:12, color:"#888", lineHeight:1.7 }}>
        고객센터 운영시간<br />평일 09:00 ~ 18:00 (점심 12:00~13:00 제외)<br />주말·공휴일 휴무
      </div>
    </div>
  );
}

// ── 업체 전용 서브페이지들 ────────────────────────────────────────

function MyJobsPage() {
  return <div style={{ background:"#fff", borderRadius:16, padding:24, textAlign:"center", color:"#aaa" }}><div style={{ fontSize:48, marginBottom:12 }}>📋</div><div style={{ fontWeight:700, color:"#333", marginBottom:6 }}>등록한 공고가 없습니다</div><div style={{ fontSize:13 }}>업체관리 탭에서 공고를 등록해보세요</div></div>;
}

function ApplicantsPage() {
  return <div style={{ background:"#fff", borderRadius:16, padding:24, textAlign:"center", color:"#aaa" }}><div style={{ fontSize:48, marginBottom:12 }}>👥</div><div style={{ fontWeight:700, color:"#333", marginBottom:6 }}>지원자가 없습니다</div><div style={{ fontSize:13 }}>공고를 등록하면 지원자가 표시됩니다</div></div>;
}

function SettlementPage() {
  return (
    <div>
      <div style={{ background:"linear-gradient(135deg,#7C4DFF,#448aff)", borderRadius:16, padding:20, marginBottom:16, color:"#fff" }}>
        <div style={{ fontSize:13, color:"rgba(255,255,255,0.7)" }}>이번달 인건비 총액</div>
        <div style={{ fontSize:32, fontWeight:900, color:"#FFD60A", marginTop:4 }}>₩ 0</div>
        <div style={{ fontSize:12, color:"rgba(255,255,255,0.5)", marginTop:4 }}>근무 완료 후 자동 집계됩니다</div>
      </div>
      <div style={{ background:"#fff", borderRadius:16, padding:24, textAlign:"center", color:"#aaa" }}><div style={{ fontSize:48, marginBottom:12 }}>💳</div><div style={{ fontSize:13 }}>정산 내역이 없습니다</div></div>
    </div>
  );
}

function BizProfilePage({ profile, user, refreshProfile, notify }) {
  const [bizName, setBizName]   = useState(profile?.bizName ?? profile?.name ?? "");
  const [bizPhone, setBizPhone] = useState(profile?.phone ?? "");
  const [bizAddr, setBizAddr]   = useState(profile?.bizAddr ?? "");
  const [saving, setSaving]     = useState(false);
  const inp = { width:"100%", boxSizing:"border-box", border:"2px solid #eee", borderRadius:10, padding:"10px 12px", fontSize:14, fontFamily:"inherit", outline:"none" };

  async function handleSave() {
    setSaving(true);
    try { await updateUserProfile(user.uid, { name:bizName, phone:bizPhone, bizAddr }); await refreshProfile(); notify("✅ 업체 정보가 수정됐습니다"); }
    catch (e) { notify("저장 실패: "+e.message, "error"); }
    finally { setSaving(false); }
  }

  return (
    <div>
      <div style={{ background:"#fff", borderRadius:16, padding:20 }}>
        <div style={{ fontWeight:900, fontSize:15, color:"#1a1a2e", marginBottom:16 }}>업체 정보</div>
        <div style={{ fontSize:12, fontWeight:700, color:"#555", marginBottom:6 }}>업체명</div>
        <input value={bizName} onChange={e=>setBizName(e.target.value)} placeholder="업체명 입력" style={{ ...inp, marginBottom:14 }} />
        <div style={{ fontSize:12, fontWeight:700, color:"#555", marginBottom:6 }}>대표 연락처</div>
        <input value={bizPhone} onChange={e=>setBizPhone(e.target.value)} placeholder="010-0000-0000" type="tel" style={{ ...inp, marginBottom:14 }} />
        <div style={{ fontSize:12, fontWeight:700, color:"#555", marginBottom:6 }}>업체 주소</div>
        <input value={bizAddr} onChange={e=>setBizAddr(e.target.value)} placeholder="서울 강남구 테헤란로 123" style={{ ...inp, marginBottom:20 }} />
        <button onClick={handleSave} disabled={saving} style={{ width:"100%", background:saving?"#aaa":"linear-gradient(90deg,#7C4DFF,#448aff)", border:"none", borderRadius:14, padding:14, color:"#fff", fontWeight:900, fontSize:15, cursor:"pointer", fontFamily:"inherit" }}>
          {saving ? "저장 중..." : "업체 정보 저장"}
        </button>
      </div>
    </div>
  );
}

// ── 하단 내비게이션 ──────────────────────────────────────────────
function BottomNav({ tab, setTab }) {
  return (
    <div style={{ position:"fixed", bottom:0, left:"50%", transform:"translateX(-50%)", width:"100%", maxWidth:390, background:"#fff", borderTop:"1px solid #f0f0f0", display:"flex", padding:"8px 0 20px", boxShadow:"0 -4px 20px rgba(0,0,0,0.08)", zIndex:40 }}>
      {[{key:"home",icon:"🏠",label:"일자리"},{key:"employer",icon:"📋",label:"업체관리"},{key:"my",icon:"👤",label:"MY"}].map(t => (
        <button key={t.key} onClick={()=>setTab(t.key)} style={{ flex:1, border:"none", background:"transparent", cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:2, padding:"6px 0" }}>
          <span style={{ fontSize:22, filter:tab===t.key?"none":"grayscale(1) opacity(0.45)", transition:"filter 0.2s" }}>{t.icon}</span>
          <span style={{ fontSize:10, fontWeight:700, color:tab===t.key?"#1a1a2e":"#bbb" }}>{t.label}</span>
          {tab===t.key && <div style={{ width:20, height:3, background:"#FFD60A", borderRadius:2 }} />}
        </button>
      ))}
    </div>
  );
}
